export enum PlayerRole {
  User = 'User',
  Moderator = 'Moderator',
  Admin = 'Admin',
}

export interface BanInfo {
  isBanned: boolean;
  reason: string;
  bannedAt: string;
  bannedUntil: string | null;
  bannedBy: string;
}

// Sent by the backend (403) when a banned user tries to log in or resume a session.
export interface BanDetails {
  banned: boolean;
  reason: string;
  bannedUntil: string | null;
  bannedBy?: string;
  message?: string;
}

export interface GameServer {
  id: string;
  name: string;
  cpuMhz: number;
  hddMb: number;
  ramMb: number;
  acquiredAt: string;
}

export enum StoreCategory {
  Server = 'Server',
  CpuUpgrade = 'CpuUpgrade',
  HddUpgrade = 'HddUpgrade',
  RamUpgrade = 'RamUpgrade',
  InternetUpgrade = 'InternetUpgrade',
  Software = 'Software',
  EncryptedHdUpgrade = 'EncryptedHdUpgrade',
}

export enum SoftwareType {
  Cracker = 'Cracker',
  PortScanner = 'PortScanner',
  FtpExploit = 'FtpExploit',
  SshExploit = 'SshExploit',
  Ddos = 'Ddos',
  PasswordHashing = 'PasswordHashing',
  Firewall = 'Firewall',
  AntiVirus = 'AntiVirus',
  DdosBreaker = 'DdosBreaker',
  Hider = 'Hider',
  Seeker = 'Seeker',
  Trojan = 'Trojan',
}

export const SOFTWARE_ROLE: Record<SoftwareType, 'ATK' | 'DEF' | 'UTL'> = {
  [SoftwareType.Cracker]: 'ATK',
  [SoftwareType.PortScanner]: 'ATK',
  [SoftwareType.FtpExploit]: 'ATK',
  [SoftwareType.SshExploit]: 'ATK',
  [SoftwareType.Ddos]: 'ATK',
  [SoftwareType.PasswordHashing]: 'DEF',
  [SoftwareType.Firewall]: 'DEF',
  [SoftwareType.AntiVirus]: 'DEF',
  [SoftwareType.DdosBreaker]: 'DEF',
  [SoftwareType.Hider]: 'UTL',
  [SoftwareType.Seeker]: 'UTL',
  [SoftwareType.Trojan]: 'ATK',
};

export interface PlayerSoftware {
  id: string;
  type: SoftwareType;
  version: number;
  isDownloaded: boolean;
  isRunning: boolean;
  purchasePrice: number;
  installedAt: string;
  downloadStartedAt?: string;
  downloadSpeedMbit?: number;
  upgradeFromVersion?: number;
}

// Lean view of a target node's installed software during a remote session.
export interface RemoteSoftware {
  id: string;
  type: SoftwareType;
  version: number;
  isRunning: boolean;
  isDownloaded: boolean;
}

export interface PendingFileDownload {
  id: string;
  fileName: string;
  content: string;
  fileSizeMb: number;
  startedAt: string;
  speedMbit: number;
}

// Base resource requirements per version unit (actual = base * version)
const SW_BASE: Record<SoftwareType, { hddMb: number; ramMb: number; cpuMhz: number }> = {
  [SoftwareType.Cracker]:          { hddMb:  8, ramMb:  64, cpuMhz: 100 },
  [SoftwareType.PortScanner]:      { hddMb:  4, ramMb:  32, cpuMhz:  50 },
  [SoftwareType.FtpExploit]:       { hddMb:  6, ramMb:  48, cpuMhz:  75 },
  [SoftwareType.SshExploit]:       { hddMb:  6, ramMb:  48, cpuMhz:  75 },
  [SoftwareType.Ddos]:             { hddMb: 10, ramMb:  80, cpuMhz: 150 },
  [SoftwareType.PasswordHashing]:  { hddMb:  5, ramMb:  40, cpuMhz:  60 },
  [SoftwareType.Firewall]:         { hddMb:  6, ramMb:  48, cpuMhz:  75 },
  [SoftwareType.AntiVirus]:        { hddMb:  6, ramMb:  32, cpuMhz:  50 },
  [SoftwareType.DdosBreaker]:      { hddMb:  6, ramMb:  48, cpuMhz:  75 },
  [SoftwareType.Hider]:            { hddMb:  5, ramMb:  40, cpuMhz:  60 },
  [SoftwareType.Seeker]:           { hddMb:  5, ramMb:  40, cpuMhz:  60 },
  [SoftwareType.Trojan]:           { hddMb:  8, ramMb:  64, cpuMhz: 120 },
};

export function getSoftwareResources(type: SoftwareType, version: number) {
  const b = SW_BASE[type];
  return {
    hddMb:  Math.ceil(b.hddMb  * version),
    ramMb:  Math.ceil(b.ramMb  * version),
    cpuMhz: Math.ceil(b.cpuMhz * version),
  };
}

// ── Software upgrade economics (mirror of Shared/SoftwareUpgrade.cs) ───────────
// Only v1.0 is bought; higher levels come from +0.1 upgrades. Cost and time both
// scale with the next version to reach. Keep these in sync with the backend.
export const SOFTWARE_BASE_V1_PRICE: Record<SoftwareType, number> = {
  [SoftwareType.Cracker]:          500,
  [SoftwareType.PortScanner]:      300,
  [SoftwareType.FtpExploit]:       800,
  [SoftwareType.SshExploit]:       1500,
  [SoftwareType.Ddos]:             600,
  [SoftwareType.PasswordHashing]:  400,
  [SoftwareType.Firewall]:         700,
  [SoftwareType.AntiVirus]:        300,
  [SoftwareType.DdosBreaker]:      500,
  [SoftwareType.Hider]:            800,
  [SoftwareType.Seeker]:           800,
  [SoftwareType.Trojan]:           900,
};

export function softwareUpgradeNextVersion(current: number): number {
  return Math.round((current + 0.1) * 10) / 10;
}

export function softwareUpgradeCost(type: SoftwareType, currentVersion: number): number {
  const next = softwareUpgradeNextVersion(currentVersion);
  return Math.ceil((SOFTWARE_BASE_V1_PRICE[type] ?? 500) * next * 0.6);
}

export function softwareUpgradeSeconds(currentVersion: number): number {
  return Math.ceil(30 * softwareUpgradeNextVersion(currentVersion));
}

export function usedHddMb(software: PlayerSoftware[]): number {
  return software.filter(s => s.isDownloaded).reduce((sum, s) => sum + getSoftwareResources(s.type, s.version).hddMb, 0);
}

// HDD occupied by stored files — shares the node's HDD with downloaded software.
export function filesHddMb(files: FileEntry[]): number {
  return files.reduce((sum, f) => sum + f.sizeMb, 0);
}

export function runningRamMb(software: PlayerSoftware[]): number {
  return software.filter(s => s.isRunning).reduce((sum, s) => sum + getSoftwareResources(s.type, s.version).ramMb, 0);
}

export function runningCpuMhz(software: PlayerSoftware[]): number {
  return software.filter(s => s.isRunning).reduce((sum, s) => sum + getSoftwareResources(s.type, s.version).cpuMhz, 0);
}

// Resources consumed by Trojan infections hosted on a node. HDD is occupied while
// the virus is stored and RAM/CPU while it runs; we count every listed infection
// (matching the server-side capacity reservation in InfectionRules).
export function infectionsHddMb(infections: { virusVersion: number }[]): number {
  return infections.reduce((sum, i) => sum + getSoftwareResources(SoftwareType.Trojan, i.virusVersion).hddMb, 0);
}

export function infectionsRamMb(infections: { virusVersion: number }[]): number {
  return infections.reduce((sum, i) => sum + getSoftwareResources(SoftwareType.Trojan, i.virusVersion).ramMb, 0);
}

export function infectionsCpuMhz(infections: { virusVersion: number }[]): number {
  return infections.reduce((sum, i) => sum + getSoftwareResources(SoftwareType.Trojan, i.virusVersion).cpuMhz, 0);
}

export interface StoreItem {
  id: string;
  name: string;
  description: string;
  category: StoreCategory;
  price: number;
  value: number;
  softwareType?: SoftwareType;
}

export enum FileType {
  Text            = 'Text',
  SoftwarePackage = 'SoftwarePackage',
  Log             = 'Log',
}

export interface FileEntry {
  id: string;
  name: string;
  type: FileType;
  sizeMb: number;
  isDeletable: boolean;
  softwarePackageType?: SoftwareType;
  softwarePackageVersion: number;
}

export interface PlayerNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  createdAt: string;
}

export interface Player {
  id: string;
  username: string;
  level: number;
  experience: number;
  reputation: number;
  credits: number;
  ipAddress: string;
  internetSpeedMBit: number;
  servers: GameServer[];
  software: PlayerSoftware[];
  // hackLog / hackedNodes / rewardedNodeIds now live in dedicated Cosmos containers
  // (internal server-side; not part of the player payload).
  multiAccountWhitelisted?: boolean;
  // Infections on this node now come from a dedicated container (gameStore.infections
  // via gameApi.getInfections()), not the player document.
  encryptedHd?: EncryptedHd;
  files: FileEntry[];
  activeMissions: MissionInstance[];
  // The once-per-day gate lives in a dedicated container; fetch via gameApi.getMissionCompletions().
  pendingFileDownloads?: PendingFileDownload[];
  ipChangeCount?: number;
  ipChangePendingAt?: string;
  ipChangePendingNewIp?: string;
  lastIpChangeAt?: string;
  shutdownUntil?: string;
  shutdownReason?: string;
  formatCount?: number;
  lastFormatAt?: string;
  notifications?: PlayerNotification[];
  isNpc: boolean;
  npcType: string;
  npcTier: number;
  role: PlayerRole;
  ban?: BanInfo;
  createdAt: string;
  lastLoginAt: string;
  firstLoginSource?: string; // "itch" | "crazygames" | "direct" (set once at signup)
  firstLoginAt?: string;
  lastLoginSource?: string;  // portal of the most recent login (refreshed each login)
  crazyGamesUserId?: string; // linked CrazyGames user id (empty = not linked)
  isGuest?: boolean;         // ephemeral per-device CrazyGames guest account
}

export interface Mission {
  id: string;
  name: string;
  description: string;
  difficulty: number;
  reward: MissionReward;
  requirements: MissionRequirements;
  timeLimit: number;
  type: MissionType;
  targetNpcId: string;
  targetNpcTier: number;
  targetNpcIp: string;
  targetNpcName: string;
  isRepeatable: boolean;
}

export interface MissionReward {
  experience: number;
  credits: number;
  reputation: number;
}

export interface MissionRequirements {
  minLevel: number;
  minHackingSkill: number;
  minStealthSkill: number;
}

export enum MissionType {
  DataTheft = 'DataTheft',
  SystemInfiltration = 'SystemInfiltration',
  DDoS = 'DDoS',
  Cryptography = 'Cryptography',
  SocialEngineering = 'SocialEngineering',
}

export enum MissionInstanceStatus {
  Active = 'Active',
  Completed = 'Completed',
  Expired = 'Expired',
}

export interface MissionInstance {
  instanceId: string;
  missionId: string;
  missionName: string;
  targetNpcId: string;
  targetNpcIp: string;
  targetNpcName: string;
  missionType: MissionType;
  startedAt: string;
  deadline: string;
  status: MissionInstanceStatus;
  missionFileId: string;
  missionFileName: string;
  reward: MissionReward;
}

export interface MissionResult {
  success: boolean;
  experienceGained: number;
  creditsGained: number;
  reputationGained: number;
  message: string;
  softwareBonusPct: number;
}

export enum HackAttackType {
  BruteForce = 'BruteForce',
  Exploit    = 'Exploit',
  DDoS       = 'DDoS',
}

export enum NodeAccessLevel {
  None    = 'None',
  FtpOnly = 'FtpOnly',
  SshOnly = 'SshOnly',
  Full    = 'Full',
}

export interface HackedNode {
  ip: string;
  username: string;
  isNpc: boolean;
  accessLevel: NodeAccessLevel;
  hackedAt: string;
  infectedVersion?: number | null;
  infectionActiveAt?: string | null;
  infectionCollectedAt?: string | null;
  infectedNodeId?: string | null;
  carriedInfectionCredits?: number;
  connectionLost?: boolean;
}

export interface Infection {
  id: string;
  attackerId: string;
  attackerUsername: string;
  virusVersion: number;
  infectedAt: string;
  activeAt: string;
}

// View-only infection entry shown for a connected target node.
export interface RemoteInfection {
  virusVersion: number;
  infectedAt: string;
  activeAt: string;
}

// ── Encrypted external backup drive (human players only) ───────────────────────
export interface HdSoftware {
  type: SoftwareType;
  version: number;
  readyAt: string;
}

export interface HdFile {
  name: string;
  type: FileType;
  sizeMb: number;
  content: string;
  readyAt: string;
}

export interface EncryptedHd {
  capacityMb: number;
  software: HdSoftware[];
  files: HdFile[];
}

export function encryptedHdUsedMb(hd: EncryptedHd): number {
  const sw    = hd.software.reduce((s, x) => s + getSoftwareResources(x.type, x.version).hddMb, 0);
  const files = hd.files.reduce((s, x) => s + x.sizeMb, 0);
  return sw + files;
}

export interface HackLogEntry {
  id: string;
  wasAttacker: boolean;
  opponentUsername: string;
  opponentIp: string;
  attackType: HackAttackType;
  success: boolean;
  creditsChanged: number;
  reputationChanged: number;
  timestamp: string;
}

export interface ScanResult {
  username: string;
  level: number;
  ipAddress: string;
  cpuMhz: number;
  hddMb: number;
  ramMb: number;
  internetSpeedMBit: number;
  visibleSoftware: PlayerSoftware[];
  softwareHidden: boolean;
}

export interface HackResult {
  success: boolean;
  targetUsername: string;
  targetIp: string;
  attackType: HackAttackType;
  successChancePct: number;
  creditsStolen: number;
  experienceGained: number;
  reputationGained: number;
  message: string;
  accessLevel?: NodeAccessLevel;
}

export interface AuthState {
  isAuthenticated: boolean;
  userId: string | null;
  username: string | null;
  token: string | null;
  role: PlayerRole | null;
}

export interface HackingAttempt {
  id: string;
  missionId: string;
  playerId: string;
  startTime: string;
  endTime?: string;
  success?: boolean;
  steps: HackingStep[];
}

export interface HackingStep {
  id: string;
  type: HackingStepType;
  description: string;
  completed: boolean;
  success: boolean;
  timestamp: string;
}

export enum HackingStepType {
  Reconnaissance = 'reconnaissance',
  Scanning = 'scanning',
  Exploitation = 'exploitation',
  PrivilegeEscalation = 'privilegeEscalation',
  DataExfiltration = 'dataExfiltration'
}

// ── Hardware formatting helpers ───────────────────────────────────────────────

export function formatMhz(mhz: number): string {
  if (mhz >= 1000) {
    const ghz = mhz / 1000;
    return `${Number.isInteger(ghz) ? ghz : ghz.toFixed(1)} GHz`;
  }
  return `${mhz} MHz`;
}

export function formatMb(mb: number): string {
  if (mb >= 1024) {
    const gb = mb / 1024;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }
  return `${mb} MB`;
}

export function formatMbit(mbit: number): string {
  if (mbit >= 1000) {
    const gbit = mbit / 1000;
    return `${Number.isInteger(gbit) ? gbit : gbit.toFixed(1)} GBit`;
  }
  return `${mbit} MBit`;
}

export function calcTotals(servers: GameServer[]) {
  return servers.reduce(
    (acc, s) => ({ cpu: acc.cpu + s.cpuMhz, hdd: acc.hdd + s.hddMb, ram: acc.ram + s.ramMb }),
    { cpu: 0, hdd: 0, ram: 0 }
  );
}

// XP needed to reach level n: (n-1)^2 * 100
export function xpForLevel(level: number): number {
  return Math.pow(level - 1, 2) * 100;
}

export function xpProgress(experience: number, level: number): { current: number; needed: number; pct: number } {
  const current = experience - xpForLevel(level);
  const needed = xpForLevel(level + 1) - xpForLevel(level);
  return { current, needed, pct: Math.min(100, Math.floor((current / needed) * 100)) };
}

// Cost to change IP: free first time, then 1 000 × 3^(count-1), capped at 80 000.
export function getIpChangeCost(completedChanges: number): number {
  return completedChanges === 0 ? 0 : Math.min(80_000, Math.round(1_000 * Math.pow(3, completedChanges - 1)));
}

// Cost to format the node: free first time, then 2 000 × 2^(count-1), capped at 64 000.
export function getFormatCost(completedFormats: number): number {
  return completedFormats === 0 ? 0 : Math.min(64_000, Math.round(2_000 * Math.pow(2, completedFormats - 1)));
}

// Returns true if an IP change is currently in progress (20-min lockout).
export function isIpChangePending(pendingAt?: string): boolean {
  if (!pendingAt) return false;
  return (Date.now() - new Date(pendingAt).getTime()) < 20 * 60 * 1000;
}

// Returns milliseconds remaining in the IP change lockout, or 0 if not locked.
export function ipChangeMsRemaining(pendingAt?: string): number {
  if (!pendingAt) return 0;
  return Math.max(0, 20 * 60 * 1000 - (Date.now() - new Date(pendingAt).getTime()));
}
